import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dumbbell } from "lucide-react";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@cliently/ui";
import { ExerciseGuideDialog } from "./ExerciseGuideDialog";

type ExerciseInfo = {
  name: string;
  nameFr: string | null;
  images: string[];
  youtubeUrl: string | null;
  instructions: string[];
  instructionsFr: string[];
  primaryMuscles: string[];
  category: string | null;
  level: string | null;
  equipment: string | null;
};

export type BlockGuideExercise = {
  title: string;
  info: ExerciseInfo;
};

type BlockGuideDialogProps = {
  open: boolean;
  onClose: () => void;
  exercises: BlockGuideExercise[];
};

/**
 * Regroupe les fiches des exercices liés à un bloc. Une seule fiche -> ouvre
 * directement le guide ; plusieurs -> propose d'abord une liste de sélection.
 */
export const BlockGuideDialog = ({
  open,
  onClose,
  exercises,
}: BlockGuideDialogProps) => {
  const { t, i18n } = useTranslation();
  const single = exercises.length === 1;
  const [activeIdx, setActiveIdx] = useState<number | null>(
    single ? 0 : null,
  );

  useEffect(() => {
    if (open) setActiveIdx(single ? 0 : null);
  }, [open, single]);

  if (exercises.length === 0) return null;

  const exerciseName = (entry: BlockGuideExercise): string =>
    entry.title?.trim() ||
    (i18n.language === "fr" && entry.info.nameFr
      ? entry.info.nameFr
      : entry.info.name);

  const first = exercises[0];
  if (single && first) {
    return (
      <ExerciseGuideDialog exercise={first.info} open={open} onClose={onClose} />
    );
  }

  const active = activeIdx != null ? exercises[activeIdx] : null;

  return (
    <>
      <Dialog
        open={open && active == null}
        onOpenChange={(v) => !v && onClose()}
      >
        <DialogContent
          aria-describedby={undefined}
          className="w-[calc(100vw-1.5rem)] max-w-md sm:w-full"
        >
          <DialogHeader>
            <DialogTitle>
              {t("programs.practicalGuide", "Guide pratique")}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-2">
            {exercises.map((entry, index) => (
              <button
                key={`${entry.title}-${index}`}
                type="button"
                onClick={() => setActiveIdx(index)}
                className="border-border/70 hover:border-primary/40 hover:bg-muted/40 flex w-full items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left text-sm font-medium transition-colors"
              >
                <span className="bg-primary/10 text-primary flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
                  <Dumbbell className="h-4 w-4" />
                </span>
                <span className="min-w-0 truncate">{exerciseName(entry)}</span>
              </button>
            ))}
          </DialogBody>
        </DialogContent>
      </Dialog>

      {active ? (
        <ExerciseGuideDialog
          exercise={active.info}
          open
          onClose={() => setActiveIdx(null)}
        />
      ) : null}
    </>
  );
};
